import React, { useEffect, useRef, useState } from 'react';
import {
	Button,
	Dropdown,
	Flex,
	Image,
	Input,
	Modal,
	Select,
	Space,
	Table,
	Typography,
	Tag,
	Tooltip,
} from 'antd';
import { DeleteOutlined, EditOutlined, FilterOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

import SH_Toolbar from '../../components/SH_Toolbar';
import productApi from '../../api/product';
import TV_product_detail from './TV_product_detail';
import categoryApi from '../../api/category';
import projectApi from '../../api/project';

const { Title } = Typography;

const TAG_COLORS = ['magenta', 'red', 'volcano', 'orange', 'gold', 'lime', 'green', 'cyan', 'blue', 'geekblue', 'purple'];

const colorById = (id = '') => {
	let hash = 0;
	for (let i = 0; i < id.length; i++) hash = (hash * 31 + id.charCodeAt(i)) >>> 0;
	return TAG_COLORS[hash % TAG_COLORS.length];
};

const EllipsisTag = ({ color, text, maxWidth = 160 }) => (
	<Tag
		color={color}
		style={{
			maxWidth,
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			whiteSpace: 'nowrap',
			display: 'inline-block',
			verticalAlign: 'middle',
		}}
		title={text}
	>
		{text}
	</Tag>
);

const TV_product = () => {
	const [dictionary, setDictionary] = useState({ categories: [], projects: [] });
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(false);
	const [total, setTotal] = useState(0);
	const refDetail = useRef();

	const [filter, setFilter] = useState({
		page: 1,
		limit: 10,
		search: '',
	});

	useEffect(() => {
		getProducts();
		getCategory().then((r) => r);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const getCategory = async () => {
		setLoading(true);
		try {
			const [categories, projects] = await Promise.all([
				categoryApi.get({ page: 1, limit: 9999 }),
				projectApi.get({ page: 1, limit: 9999 }),
			]);

			setDictionary({
				categories: categories?.data?.data?.items?.map((x) => ({ ...x, label: x?.names?.vi })) || [],
				projects: projects?.data?.data?.items?.map((x) => ({ ...x, label: x?.names?.vi })) || [],
			});
		} finally {
			setLoading(false);
		}
	};

	const getProducts = (_filter = filter) => {
		setLoading(true);
		productApi
			.get({
				..._filter,
				page: _filter?.page || 1,
				limit: _filter?.limit || 10,
			})
			.then((res) => {
				setProducts(res?.data?.data?.items || []);
				setTotal(res?.data?.data?.pagination?.total || 0);
			})
			.finally(() => setLoading(false));
	};

	const deleteItem = (event) => {
		setLoading(true);
		productApi
			.delete(event._id)
			.then(() => getProducts())
			.finally(() => setLoading(false));
	};

	const conFirm = (record) => {
		return Modal.confirm({
			title: 'Xác nhận xóa',
			content: 'Bạn có chắc chắn muốn xóa bản ghi này không?',
			okText: 'Xóa',
			cancelText: 'Hủy',
			okButtonProps: { danger: true },
			onOk: () => deleteItem(record),
		});
	};

	const showModal = () => refDetail.current.create();

	const handlePageChange = (page, pageSize) => {
		setFilter((prevState) => ({ ...prevState, page, limit: pageSize }));
		getProducts({ ...filter, page, limit: pageSize });
	};

	const columns = [
		{
			title: 'Tên sản phẩm',
			dataIndex: 'names',
			key: 'names_vi',
			width: 220,
			ellipsis: true,
			render: (text) => <span title={text?.vi}>{text?.vi}</span>,
		},
		// {
		// 	title: 'Tên sản phẩm (EN)',
		// 	dataIndex: 'names',
		// 	key: 'names_en',
		// 	width: 220,
		// 	ellipsis: true,
		// 	render: (text) => <span title={text?.en}>{text?.en}</span>,
		// },
		// {
		// 	title: 'Dự án',
		// 	dataIndex: 'project',
		// 	key: 'project',
		// 	width: 180,
		// 	ellipsis: true,
		// 	render: (project) => {
		// 		const name = project?.names?.vi || project?.names?.en;
		// 		if (!name) return <span>-</span>;
		// 		return <EllipsisTag color={colorById(project?._id)} text={name} maxWidth={160} />;
		// 	},
		// },
		{
			title: 'Loại sản phẩm',
			dataIndex: 'categoryList',
			key: 'categoryList',
			width: 260,
			ellipsis: true,
			render: (categoryList) => {
				const list = (categoryList || []).filter(Boolean);
				if (list.length === 0) return <span>-</span>;

				const shown = list.slice(0, 2);
				const rest = list.slice(2);

				return (
					<Space size={[6, 6]} wrap>
						{shown.map((c) => {
							const label = c?.names?.vi || c?.names?.en || '-';
							return <EllipsisTag key={c?._id} color={colorById(c?._id)} text={label} maxWidth={110} />;
						})}

						{rest.length > 0 ? (
							<Tooltip
								title={rest
									.map((c) => c?.names?.vi || c?.names?.en)
									.filter(Boolean)
									.join(', ')}
							>
								<Tag style={{ maxWidth: 80 }}>+{rest.length}</Tag>
							</Tooltip>
						) : null}
					</Space>
				);
			},
		},
		{
			title: 'Ảnh',
			dataIndex: 'images',
			key: 'images',
			width: 120,
			render: (images) => {
				const urls = (images || []).map((x) => x?.url).filter(Boolean);
				if (urls.length === 0) return <span>-</span>;

				return (
					<div style={{ position: 'relative', width: 90, height: 60 }}>
						<Image.PreviewGroup>
							<Image
								width={90}
								height={60}
								style={{ objectFit: 'cover', borderRadius: 10 }}
								src={urls[0]}
								alt=""
								preview={{ mask: 'Xem' }}
							/>
							{urls.slice(1).map((u, idx) => (
								<Image key={idx} src={u} style={{ display: 'none' }} />
							))}
						</Image.PreviewGroup>

						{urls.length > 1 ? (
							<div
								style={{
									position: 'absolute',
									right: -6,
									top: -6,
									background: 'rgba(0,0,0,0.75)',
									color: '#fff',
									fontSize: 11,
									fontWeight: 700,
									padding: '2px 6px',
									borderRadius: 999,
									lineHeight: '16px',
									boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
									pointerEvents: 'none',
								}}
							>
								+{urls.length - 1}
							</div>
						) : null}
					</div>
				);
			},
		},
		{
			title: 'Ngày tạo',
			dataIndex: 'createdAt',
			key: 'createdAt',
			width: 170,
			render: (createdAt) => <span>{createdAt ? dayjs(createdAt).format('HH:mm A DD/MM/YYYY') : ''}</span>,
			sorter: (a, b) => dayjs(a.createdAt).valueOf() - dayjs(b.createdAt).valueOf(),
			sortDirections: ['descend', 'ascend'],
		},
		{
			title: 'Thao tác',
			key: 'action',
			width: 170,
			fixed: 'right',
			render: (_, record) => (
				<Space size={8} wrap>
					<Button size="normal" onClick={() => refDetail.current.update(record)} icon={<EditOutlined />}>
						Sửa
					</Button>
					<Button size="normal" onClick={() => conFirm(record)} type="dashed" danger icon={<DeleteOutlined />}>
						Xoá
					</Button>
				</Space>
			),
		},
	];

	return (
		<>
			<Flex align="center" justify="space-between" wrap="wrap" gap={12} style={{ marginBottom: 12 }}>
				<Title level={2} style={{ margin: 0 }}>
					Sản phẩm
				</Title>
			</Flex>

			<SH_Toolbar
				left={() => (
					<Button icon={<PlusOutlined />} type="primary" onClick={showModal}>
						Thêm sản phẩm
					</Button>
				)}
				right={() => (
					<Flex gap={8} wrap="wrap" align="center" style={{ maxWidth: '100%' }}>
						<Input.Search
							allowClear
							style={{ width: 260, maxWidth: '100%' }}
							placeholder="Nhập tên sản phẩm"
							onChange={(e) => {
								getProducts({ ...filter, search: e.target.value });
								setFilter((prevState) => ({ ...prevState, search: e.target.value }));
							}}
						/>

						<Dropdown
							trigger={['click']}
							placement="bottomRight"
							dropdownRender={() => (
								<div style={{ width: 320, maxWidth: 'calc(100vw - 24px)', background: '#fff', padding: 12, borderRadius: 12 }}>
									<Space direction="vertical" size={12} style={{ width: '100%' }}>
										{/* <div>
											<div style={{ fontWeight: 600, marginBottom: 6 }}>Dự án</div>
											<Select
												allowClear
												style={{ width: '100%' }}
												value={filter.projectId}
												fieldNames={{ label: 'label', value: '_id' }}
												options={dictionary?.projects}
												placeholder="Chọn dự án"
												onChange={(e) => {
													getProducts({ ...filter, projectId: e });
													setFilter((prevState) => ({ ...prevState, projectId: e }));
												}}
											/>
										</div> */}

										<div>
											<div style={{ fontWeight: 600, marginBottom: 6 }}>Loại sản phẩm</div>
											<Select
												mode="multiple"
												allowClear
												style={{ width: '100%' }}
												value={filter.categories}
												fieldNames={{ label: 'label', value: '_id' }}
												options={dictionary?.categories}
												placeholder="Chọn loại sản phẩm"
												onChange={(e) => {
													getProducts({ ...filter, categories: e });
													setFilter((prevState) => ({ ...prevState, categories: e }));
												}}
											/>
										</div>
									</Space>
								</div>
							)}
						>
							<Button icon={<FilterOutlined />}>Lọc</Button>
						</Dropdown>
					</Flex>
				)}
			/>

			<Table
				rowKey="_id"
				tableLayout="fixed"
				pagination={{
					current: filter.page,
					pageSize: filter.limit,
					total,
					onChange: handlePageChange,
					showSizeChanger: true,
				}}
				className="mt-2"
				loading={loading}
				columns={columns}
				dataSource={products}
				scroll={{ x: 1300 }}
			/>

			<TV_product_detail ref={refDetail} getProducts={getProducts} categories={dictionary?.categories} projects={dictionary?.projects} />
		</>
	);
};

export default TV_product;
